import { RADashboardTabId } from "@/constants/ReportDashboard/tab-enums"
import { useTheme } from "@/contexts/RADashboardThemeContext/hooks"
import { RADashboardLayout } from "@/interfaces/raDashboard.interface"
import { Add, DeleteOutline, DragIndicator, HomeOutlined, MoreVert } from '@mui/icons-material'
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState } from "react"

type DashboardGuiTabsProps = {
  layout: RADashboardLayout | undefined
  isLocked: boolean
  setLayout: (newLayout: RADashboardLayout) => void
  selectedTab: string | null
  onSelectTab: (tabId: string) => void
}

const DashboardGuiTabs: React.FC<DashboardGuiTabsProps> = ({ layout, isLocked, setLayout, selectedTab, onSelectTab }) => {
  const theme = useTheme()
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; tabId: string } | null>(null)
  const [dragTabId, setDragTabId] = useState<string | null>(null)

  const tabsOrder: string[] = layout?.tabsOrder ?? []
  const inactiveTabs = Object.values(RADashboardTabId).filter(id => !tabsOrder.includes(id))

  const updateLayout = (patch: Partial<typeof layout>) => {
    if (!layout) return
    setLayout({ ...layout, ...patch })
  }

  const handleLabelChange = (tabId: string, label: string) => {
    updateLayout({
      tabSettings: {
        ...layout?.tabSettings,
        [tabId]: { ...layout?.tabSettings?.[tabId], label },
      },
    })
  }

  // Directional reorder: dropping a tab dragged downward lands it after the
  // target; dragged upward lands it before. Both ends stay reachable by dropping
  // anywhere on the target row. (toIndex is read before removal on purpose — the
  // resulting one-off when fromIndex < toIndex is what gives the "after" placement.)
  const reorderTabs = (fromTabId: string, toTabId: string) => {
    if (fromTabId === toTabId) return
    const newOrder = [...tabsOrder]
    const fromIndex = newOrder.indexOf(fromTabId)
    const toIndex = newOrder.indexOf(toTabId)
    if (fromIndex < 0 || toIndex < 0) return

    newOrder.splice(fromIndex, 1)
    newOrder.splice(toIndex, 0, fromTabId)
    updateLayout({ tabsOrder: newOrder })
  }

  const enableTab = (tabId: string) => {
    updateLayout({
      tabsOrder: [...tabsOrder, tabId],
      tabLayouts: { ...layout?.tabLayouts, [tabId]: { cardsOrder: [] } },
    })
  }

  const disableTab = (tabId: string) => {
    const { [tabId]: _removedSettings, ...remainingSettings } = layout?.tabSettings ?? {};
    const { [tabId]: _removedLayout, ...remainingLayouts } = layout?.tabLayouts ?? {};
    const newOrder = tabsOrder.filter(id => id !== tabId);
    let updateObj: Partial<RADashboardLayout> = {
      tabsOrder: newOrder,
      tabSettings: remainingSettings,
      tabLayouts: remainingLayouts,
    };
    if (newOrder.length > 0 && tabId === layout?.initialTab) {
      updateObj.initialTab = newOrder[0];
    }
    updateLayout(updateObj);
  }

  const setInitialTab = (tabId: string) => updateLayout({ initialTab: tabId })
  const closeMenu = () => setMenuAnchor(null)
  const openMenuTabId = menuAnchor?.tabId

  const renderActiveRow = (tabId: string) => (
    <ListItem key={tabId} disableGutters
      onClick={() => onSelectTab(tabId)}
      onDragOver={(e) => { if (dragTabId) e.preventDefault() }}
      onDrop={() => { if (dragTabId) { reorderTabs(dragTabId, tabId); setDragTabId(null) } }}
      sx={{
        gap: 1,
        cursor: 'pointer',
        borderRadius: 1,
        opacity: dragTabId === tabId ? 0.4 : 1,
        outline: selectedTab === tabId ? '2px solid' : 'none',
        outlineColor: 'primary.main', px: selectedTab === tabId ? 0.5 : 0
      }}>
      {/* Drag handle — grab here to reorder. Don't let clicks bubble to row selection. */}
      <Box
        draggable={!isLocked}
        onDragStart={(e) => { setDragTabId(tabId); e.stopPropagation() }}
        onDragEnd={() => setDragTabId(null)}
        onClick={(e) => e.stopPropagation()}
        title="Drag to reorder"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          cursor: isLocked ? 'default' : 'grab',
          '&:active': { cursor: isLocked ? 'default' : 'grabbing' },
        }}
      >
        <DragIndicator sx={{ fontSize: 18 }} />
      </Box>

      <TextField
        size="small"
        fullWidth
        disabled={isLocked}
        value={layout?.tabSettings?.[tabId]?.label ?? ''}
        onChange={(e) => handleLabelChange(tabId, e.target.value)}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        {layout?.initialTab === tabId && <HomeOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
        <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
          ({tabId})
        </Typography>
      </Box>

      <IconButton size="small" title="Tab actions" onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, tabId }) }}>
        <MoreVert fontSize="small" />
      </IconButton>
    </ListItem>
  )

  const renderInactiveRow = (tabId: string) => (
    <ListItem key={tabId} disableGutters sx={{ gap: 1, opacity: 0.75 }}>
      <Box sx={{ width: 28, flexShrink: 0 }} />

      <TextField
        size="small"
        fullWidth
        disabled
        value={layout?.tabSettings?.[tabId]?.label ?? ''}
      />

      <Typography variant="caption" noWrap sx={{ color: 'text.secondary', flexShrink: 0 }}>
        ({tabId})
      </Typography>

      {!isLocked && (
        <IconButton size="small" onClick={() => enableTab(tabId)} title="Add tab" color="primary">
          <Add fontSize="small" />
        </IconButton>
      )}
    </ListItem>
  )

  return (
    <>
      <Box sx={{
        width: 340,
        flexShrink: 0,
        borderRadius: '8px',
        border: `1px solid ${theme.border}`,
        p: 2.5,
        boxSizing: 'border-box',
        backgroundColor: theme.inputBackground,
      }}>
        <List disablePadding>
          {tabsOrder.map(tabId => renderActiveRow(tabId))}
        </List>

        {inactiveTabs.length > 0 && tabsOrder.length > 0 && (
          <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
        )}

        <List disablePadding>
          {inactiveTabs.map(tabId => renderInactiveRow(tabId))}
        </List>
      </Box>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {!isLocked && (
          <MenuItem onClick={() => { setInitialTab(openMenuTabId!); closeMenu() }}>
            <ListItemIcon>
              <HomeOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Set as initial tab</ListItemText>
          </MenuItem>
        )}

        {!isLocked && (
          <MenuItem onClick={() => { disableTab(openMenuTabId!); closeMenu() }} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove tab</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

export default DashboardGuiTabs
